<script setup lang="ts">
/**
 * UI-kit component
 * Category: Cards
 * Name: CardIcon
 * FIGMA: https://www.figma.com/design/NgI2cVJkWODrgnOBHO3Zed/Strapi-Builder--DS-?node-id=19-4113
 *
 * Local implementation of the Figma DS CardIcon missing from @brighterly/lib-lumee-ui.
 * layout=horizontal — the compact card with a BrandShapeIcon plate left of the title
 * (mobile scale at every breakpoint, as used by SectionRatingsGauge).
 * layout=vertical — plate above title + description, 8 surfaces, responsive per Figma:
 * mobile scale (plate md, title-xl, radius 24) below 740px, desktop scale (plate lg,
 * heading-s, radius 32) from the tablet breakpoint (no tablet variant by design).
 * On the six color surfaces the plate color equals the surface; white/gray fall back
 * to the `color` prop (ultraviolet by default). The glyph is the `icon` prop (DS Icon)
 * or the `#icon` slot (e.g. AppIconStrapi for Strapi IconHub fields); with neither,
 * the BrandShapeIcon plate is not rendered (Figma showBrandShapeIcon=false).
 * `#top` slot = the Figma topSlot (image area above the text: full width, radius 20
 * mobile / 24 on desktop-scale cards, surface/brand placeholder, clips content);
 * the consumer owns the media height. Title is optional per Figma showTitle.
 * descriptionClamp=false lifts the 7/10-line description clamp (SectionImpactCards
 * main card). desktopScaleFrom='desktop' keeps the mobile scale through the tablet
 * band and switches to the desktop scale at 1024+ (Figma pairing where tablet cards
 * are deviceWidth=mobile); default 'tablet' switches at 740 as before.
 * bottomSlot from the Figma component is not implemented (no consumer yet).
 */
// types
import type { CardIconProps } from '../types/interfaces'
// hooks
import { useCardIcon } from '../composables/use-card-icon.hook'
// components
import { Icon } from '@brighterly/lib-lumee-ui'
import { BrandShapeIcon } from '@UI/Brand/BrandShapeIcon'

defineOptions({
  name: 'CardIcon',
})

const props = withDefaults(defineProps<CardIconProps>(), {
  title: '',
  description: '',
  layout: 'horizontal',
  surface: 'white',
  icon: undefined,
  shape: 'square',
  color: 'ultraviolet',
  descriptionClamp: true,
  desktopScaleFrom: 'tablet',
})

const {
  refPlateColor,
  refTitleClass,
} = useCardIcon({ props })
</script>

<template>
  <article
    class="card-icon"
    :class="[
      `card-icon--${layout}`,
      `card-icon--${surface}`,
      {
        'card-icon--scale-desktop': desktopScaleFrom === 'desktop',
        'card-icon--description-unclamped': !descriptionClamp,
      },
    ]"
  >
    <div
      v-if="$slots.top"
      class="card-icon__top"
    >
      <slot name="top" />
    </div>
    <div class="card-icon__body">
      <BrandShapeIcon
        v-if="icon || $slots.icon"
        class="card-icon__plate"
        :shape="shape"
        :color="refPlateColor"
        size="md"
      >
        <slot name="icon">
          <Icon
            v-if="icon"
            :name="icon"
            size="lg"
            weight="solid"
            color="inverse"
          />
        </slot>
      </BrandShapeIcon>
      <div class="card-icon__text">
        <h3
          v-if="title"
          class="card-icon__title"
          :class="refTitleClass"
        >
          {{ title }}
        </h3>
        <p
          v-if="description"
          class="card-icon__description btly-text-body-l"
        >
          {{ description }}
        </p>
      </div>
    </div>
  </article>
</template>

<style lang="scss" scoped src="../styles/_card-icon.scss" />
